import React, { useState, useRef, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaMapMarkedAlt, FaFilter } from "react-icons/fa";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import useDrawerTransition from "../../Hooks/useDrawerTransition";
import SearchInput from "../shared/SearchInput";
import { getDeviceCupNumber, deviceHasGrinder } from "../../utils/deviceUtils";

const ICON_URLS = {
  "Çayset-Fincanda": require("../../Assets/icons/caykahvebirarada.png"),
  Çayset: require("../../Assets/icons/cayfiltremakinesi.png"),
  Fincanda: require("../../Assets/icons/dortludegirmenli.png"),
  Espresso: require("../../Assets/icons/espresso_machine.png"),
  // Eskiden dış CDN'den (flaticon) geliyordu; yüklenmezse marker görünmez
  // oluyordu — yerel görsel kullanılır.
  Diğer: require("../../Assets/icons/tea_coffee_machine.png"),
};

const MODEL_ORDER = ["Çayset", "Fincanda", "Çayset-Fincanda", "Espresso", "Diğer"];

const ICON_SIZE = 40;
const SELECTED_ICON_SIZE = 56;
// Aynı koordinattaki cihazlar üst üste binip tek marker gibi görünüyordu;
// ikonlar piksel uzayında bu yarıçapta bir halkaya dağıtılır (zoom'dan bağımsız).
const OVERLAP_RING_PX = 26;

function getDeviceModel(device) {
  const name = String(device?.deviceInfo?.deviceName ?? device?.deviceName ?? "");
  if (/çayset-fincanda/i.test(name)) return "Çayset-Fincanda";
  if (/fincanda/i.test(name)) return "Fincanda";
  if (/çayset|çay-filtre/i.test(name)) return "Çayset";
  if (/espresso/i.test(name)) return "Espresso";
  return "Diğer";
}

// Server componentLegacyNormalizer kurallarıyla aynı: saf Çayset değirmenli
// sayılmaz, 5 gözlü Çayset-Fincanda'da değirmen her zaman vardır.
function deviceHasGrinderForMap(device) {
  const model = getDeviceModel(device);
  if (model === "Çayset" || model === "Espresso") return false;
  if (model === "Çayset-Fincanda" && getDeviceCupNumber(device) === 5) return true;
  return deviceHasGrinder(device);
}

const iconCache = new Map();
function getDeviceIcon(model, { selected = false, dx = 0, dy = 0 } = {}) {
  const size = selected ? SELECTED_ICON_SIZE : ICON_SIZE;
  const key = `${model}|${size}|${dx}|${dy}`;
  if (!iconCache.has(key)) {
    iconCache.set(
      key,
      new L.Icon({
        iconUrl: ICON_URLS[model] || ICON_URLS.Diğer,
        iconSize: [size, size],
        iconAnchor: [size / 2 - dx, size / 2 - dy],
        popupAnchor: [dx, dy - size / 2],
        className: selected ? "drop-shadow-lg" : "",
      })
    );
  }
  return iconCache.get(key);
}

const hasLocation = (d) =>
  Number.isFinite(Number(d?.locationInfo?.coordinates?.latitude)) &&
  Number.isFinite(Number(d?.locationInfo?.coordinates?.longitude)) &&
  !(Number(d.locationInfo.coordinates.latitude) === 0 && Number(d.locationInfo.coordinates.longitude) === 0);

const latLngOf = (d) => [
  Number(d.locationInfo.coordinates.latitude),
  Number(d.locationInfo.coordinates.longitude),
];

function getTimeAgo(date) {
  if (!date) return "-";
  const diffMs = new Date() - new Date(date);
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
  const diffMinutes = Math.floor((diffMs / (1000 * 60)) % 60);
  if (diffDays > 0) return `${diffDays} gün ${diffHours} saat önce`;
  if (diffHours > 0) return `${diffHours} saat ${diffMinutes} dakika önce`;
  if (diffMinutes > 0) return `${diffMinutes} dakika önce`;
  return "Az önce";
}

export default function DeviceMapDrawer({ isOpen, onClose, devices: devicesProp }) {
  // Tam ekran panel — yandan kaymak yerine yumuşak açılır/kapanır; kapanış
  // animasyonu bitene kadar cihaz listesi dondurulur.
  const { mounted, entered, data: devices } = useDrawerTransition(
    Boolean(isOpen && devicesProp && devicesProp.length > 0),
    devicesProp
  );
  if (!mounted || !devices || devices.length === 0) return null;

  const validDevices = devices.filter(hasLocation);
  const unknownCount = devices.length - validDevices.length;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex transition-opacity duration-300 ease-in-out ${entered ? "opacity-100" : "opacity-0"}`}
    >
      <div className="flex-1 bg-black/50" onClick={onClose} />
      <div
        className={`fixed inset-0 bg-gray-50 shadow-xl flex flex-col max-w-full w-full h-full z-50 transform transition-transform duration-300 ease-in-out ${entered ? "scale-100" : "scale-95"}`}
      >
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-white">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg sm:rounded-xl shadow-sm border border-blue-100">
                <FaMapMarkedAlt className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <h2 className="text-base sm:text-xl font-bold text-gray-900">Müşterideki Cihazların Konumları</h2>
              <span className="px-2.5 py-1 bg-blue-50/80 border border-blue-100/80 text-blue-700 rounded-lg text-xs sm:text-sm font-semibold">{devices.length} cihaz</span>
            </div>
            <div className="text-xs sm:text-sm text-gray-500 mt-1">
              Müşterideki cihazlardan {unknownCount} tanesinin konumu bilinmiyor.
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-all duration-200">
            <FaTimes className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4 sm:p-6 bg-gray-50">
          <MultiDeviceMap devices={devices} entered={entered} />
        </div>
      </div>
    </div>,
    document.body
  );
}

const EMPTY_FILTERS = { models: [], cups: [], grinder: [] };

function toggleIn(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

// Görünen cihaz kümesi değiştiğinde (arama/filtre) haritayı onlara sığdırır.
// MapContainer'ın center/zoom prop'ları yalnız ilk açılışta okunur; sonrası
// için bu bileşen gerekir.
function FitToDevices({ devices, boundsKey }) {
  const map = useMap();
  useEffect(() => {
    if (devices.length === 0) return;
    if (devices.length === 1) {
      map.setView(latLngOf(devices[0]), 15, { animate: true });
      return;
    }
    map.fitBounds(L.latLngBounds(devices.map(latLngOf)), { padding: [40, 40], maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundsKey, map]);
  return null;
}

// Harita, drawer açılış animasyonu (scale-95 → 100) sırasında oluşturulur;
// animasyon bitince boyut yeniden ölçülmezse kenarlarda karo/marker eksik kalır.
function InvalidateOnEnter({ entered }) {
  const map = useMap();
  useEffect(() => {
    if (!entered) return undefined;
    const t = setTimeout(() => map.invalidateSize(), 320);
    return () => clearTimeout(t);
  }, [entered, map]);
  return null;
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1.5 rounded-lg text-xs font-SemiBold border transition-all ${
        active
          ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
          : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
      }`}
    >
      {children}
    </button>
  );
}

function MultiDeviceMap({ devices, entered }) {
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const mapRef = useRef(null);
  const filterRef = useRef(null);

  useEffect(() => {
    if (!filterOpen) return undefined;
    const onDown = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [filterOpen]);

  // Seçenekler eldeki veriden türetilir — olmayan model/göz gösterilmez.
  const { modelOptions, cupOptions } = useMemo(() => {
    const models = new Set();
    const cups = new Set();
    (devices || []).forEach((d) => {
      models.add(getDeviceModel(d));
      const cup = getDeviceCupNumber(d);
      if (cup > 0) cups.add(cup);
    });
    return {
      modelOptions: MODEL_ORDER.filter((m) => models.has(m)),
      cupOptions: [...cups].sort((a, b) => a - b),
    };
  }, [devices]);

  const activeFilterCount = filters.models.length + filters.cups.length + filters.grinder.length;

  const filteredDevices = useMemo(() => {
    const s = search.trim().toLocaleLowerCase("tr-TR");
    return (devices || []).filter((d) => {
      if (s) {
        const firm = (d.firm?.firmName || "").toLocaleLowerCase("tr-TR");
        const serial = (d.serialNo || "").toLocaleLowerCase("tr-TR");
        if (!firm.includes(s) && !serial.includes(s)) return false;
      }
      if (filters.models.length && !filters.models.includes(getDeviceModel(d))) return false;
      if (filters.cups.length && !filters.cups.includes(getDeviceCupNumber(d))) return false;
      if (filters.grinder.length) {
        const key = deviceHasGrinderForMap(d) ? "grinder" : "noGrinder";
        if (!filters.grinder.includes(key)) return false;
      }
      return true;
    });
  }, [devices, search, filters]);

  const validDevices = useMemo(() => filteredDevices.filter(hasLocation), [filteredDevices]);
  const invalidDevices = useMemo(() => filteredDevices.filter((d) => !hasLocation(d)), [filteredDevices]);

  // Aynı noktadaki cihazlara halka üzerinde piksel kayması ver.
  const offsets = useMemo(() => {
    const groups = new Map();
    validDevices.forEach((d) => {
      const [lat, lng] = latLngOf(d);
      const k = `${lat.toFixed(5)},${lng.toFixed(5)}`;
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(d._id);
    });
    const out = {};
    groups.forEach((ids) => {
      if (ids.length === 1) {
        out[ids[0]] = { dx: 0, dy: 0 };
        return;
      }
      ids.forEach((id, i) => {
        const a = (2 * Math.PI * i) / ids.length - Math.PI / 2;
        out[id] = {
          dx: Math.round(Math.cos(a) * OVERLAP_RING_PX),
          dy: Math.round(Math.sin(a) * OVERLAP_RING_PX),
        };
      });
    });
    return out;
  }, [validDevices]);

  const boundsKey = useMemo(() => validDevices.map((d) => d._id).join(","), [validDevices]);

  if (!devices || devices.length === 0) return null;
  const defaultCenter = { lat: 39.9334, lng: 32.8597 };

  // Eskiden seçim yapılınca diğer cihazlar haritadan siliniyordu ("harita
  // kayınca bazıları kayboluyor"). Artık filtreye uyan herkes görünür; seçim
  // yalnız vurgular ve haritayı o cihaza götürür.
  const handleDeviceClick = (device) => {
    if (!hasLocation(device)) return;
    const willSelect = !selectedDevices.includes(device._id);
    setSelectedDevices((prev) => toggleIn(prev, device._id));
    if (willSelect && mapRef.current) {
      mapRef.current.setView(latLngOf(device), Math.max(mapRef.current.getZoom(), 15), { animate: true });
    }
  };

  const isSelected = (id) => selectedDevices.includes(id);

  const filterGroups = [
    {
      key: "models",
      label: "Model",
      options: modelOptions.map((m) => ({ id: m, text: m })),
    },
    {
      key: "cups",
      label: "Göz Sayısı",
      options: cupOptions.map((c) => ({ id: c, text: `${c} göz` })),
    },
    {
      key: "grinder",
      label: "Değirmen",
      options: [
        { id: "grinder", text: "Değirmenli" },
        { id: "noGrinder", text: "Değirmensiz" },
      ],
    },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <div className="lg:col-span-1">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-SemiBold text-gray-900">Cihaz Listesi</h3>
          <span className="text-xs text-gray-500">
            {validDevices.length} konumlu / {filteredDevices.length}
          </span>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Firma adı veya seri no ara..."
            wrapperClassName="flex-1"
          />
          <div className="relative" ref={filterRef}>
            <button
              type="button"
              onClick={() => setFilterOpen((o) => !o)}
              className={`relative flex items-center gap-1.5 px-3 py-2 sm:py-2.5 rounded-lg sm:rounded-xl border text-xs sm:text-sm font-Medium transition-colors ${
                activeFilterCount > 0 || filterOpen
                  ? "bg-indigo-50 border-indigo-200 text-indigo-700"
                  : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
              title="Filtrele"
            >
              <FaFilter className="w-3.5 h-3.5" />
              <span>Filtre</span>
              {activeFilterCount > 0 && (
                <span className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-600 text-white text-[10px] font-Bold flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {filterOpen && (
              <div className="absolute right-0 lg:left-0 lg:right-auto top-full mt-2 z-[1000] w-72 bg-white border border-gray-200 rounded-xl shadow-xl p-4 space-y-4">
                {filterGroups.map((group) => (
                  <div key={group.key}>
                    <div className="text-xs font-SemiBold text-gray-500 mb-2">{group.label}</div>
                    {group.options.length === 0 ? (
                      <div className="text-xs text-gray-400">Seçenek yok</div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {group.options.map((opt) => (
                          <FilterChip
                            key={opt.id}
                            active={filters[group.key].includes(opt.id)}
                            onClick={() =>
                              setFilters((p) => ({ ...p, [group.key]: toggleIn(p[group.key], opt.id) }))
                            }
                          >
                            {opt.text}
                          </FilterChip>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setFilters(EMPTY_FILTERS)}
                    disabled={activeFilterCount === 0}
                    className="text-xs font-SemiBold text-indigo-600 hover:text-indigo-800 disabled:text-gray-300"
                  >
                    Temizle
                  </button>
                  <button
                    type="button"
                    onClick={() => setFilterOpen(false)}
                    className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-SemiBold hover:bg-indigo-700"
                  >
                    Tamam
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="space-y-2 h-[calc(70vh)] overflow-y-auto">
          {filteredDevices.length === 0 && (
            <div className="text-sm text-gray-500 text-center py-8">Filtreye uyan cihaz yok.</div>
          )}
          {validDevices.map((device) => (
            <div
              key={device._id}
              onClick={() => handleDeviceClick(device)}
              className={`p-3 rounded-xl border cursor-pointer transition-all duration-200 flex items-center gap-2 shadow-sm ${
                isSelected(device._id)
                  ? "border-blue-300 bg-blue-50/80"
                  : "border-gray-200 hover:border-blue-300 hover:bg-blue-50/40 bg-white"
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected(device._id)}
                onChange={() => handleDeviceClick(device)}
                onClick={(e) => e.stopPropagation()}
                className="mr-2 w-4 h-4 accent-blue-600"
              />
              <div className="flex-1">
                <p className="font-SemiBold text-sm text-gray-900">{device.deviceInfo?.deviceName || "İsimsiz Cihaz"}</p>
                <p className="text-xs text-gray-500">{device.firm?.firmName || "Firma bilgisi yok"}</p>
                <p className="text-xs text-gray-400 mt-1">{device.serialNo}</p>
              </div>
              <div className={`w-4 h-4 rounded-full shrink-0 ${device.deviceInfo?.connection?.status ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
            </div>
          ))}
          {invalidDevices.map((device) => (
            <div key={device._id} className="p-3 rounded-xl border border-gray-200 bg-gray-50 flex items-center gap-2 opacity-70 cursor-not-allowed select-none">
              <input type="checkbox" checked={false} disabled className="mr-2 w-4 h-4 accent-blue-600" />
              <div className="flex-1">
                <p className="font-SemiBold text-sm text-gray-500">{device.deviceInfo?.deviceName || "İsimsiz Cihaz"}</p>
                <p className="text-xs text-gray-400">{device.firm?.firmName || "Firma bilgisi yok"}</p>
                <p className="text-xs text-gray-400 mt-1">{device.serialNo}</p>
                <p className="text-xs text-red-500 mt-1">Konum yok</p>
              </div>
              <div className="w-4 h-4 rounded-full bg-gray-300 shrink-0" />
            </div>
          ))}
        </div>
      </div>
      <div className="lg:col-span-3">
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm" style={{ height: "calc(100vh - 180px)" }}>
          <MapContainer
            center={[defaultCenter.lat, defaultCenter.lng]}
            zoom={6}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom
            // react-leaflet v4: whenCreated kaldırıldı, map instance ref ile alınır
            ref={mapRef}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <InvalidateOnEnter entered={entered} />
            <FitToDevices devices={validDevices} boundsKey={boundsKey} />
            {validDevices.map((device) => {
              const selected = isSelected(device._id);
              return (
                <Marker
                  key={device._id}
                  position={latLngOf(device)}
                  icon={getDeviceIcon(getDeviceModel(device), { selected, ...offsets[device._id] })}
                  zIndexOffset={selected ? 1000 : 0}
                >
                  <Popup>
                    <div className="min-w-[280px] max-w-[340px] bg-white text-gray-800 rounded-xl p-3">
                      <div className="font-Bold text-base text-blue-600 mb-2.5 text-center">
                        {device.deviceInfo?.deviceName || "İsimsiz Cihaz"}
                      </div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm mb-2">
                        <span className="font-SemiBold text-gray-500">Doğruluk:</span>
                        <span className="text-gray-900">{Number(device.locationInfo.accuracy || 0).toFixed(2)} metre</span>
                        <span className="font-SemiBold text-gray-500">Firma:</span>
                        <span className="text-gray-900 break-words">{device.firm?.firmName || "Bilinmiyor"}</span>
                        <span className="font-SemiBold text-gray-500">Seri No:</span>
                        <span className="text-gray-900">{device.serialNo}</span>
                        <span className="font-SemiBold text-gray-500">Göz / Değirmen:</span>
                        <span className="text-gray-900">
                          {getDeviceCupNumber(device) || "—"} göz · {deviceHasGrinderForMap(device) ? "Değirmenli" : "Değirmensiz"}
                        </span>
                        <span className="font-SemiBold text-gray-500">Son Güncelleme:</span>
                        <span className="text-gray-900">
                          {device.locationInfo.updatedInfo ? new Date(device.locationInfo.updatedInfo).toLocaleString("tr-TR") : "Bilinmiyor"}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 text-right mt-2">{getTimeAgo(device.locationInfo.updatedInfo)}</div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}
